<template>
	<div class="regulatory-assistant-answer">
		<div class="heading">
			<p class="section-label">{{ t(`regulatoryAssistant.answer.titles.${response.mode}`) }}</p>
			<p v-if="answer.summary" class="summary">{{ answer.summary }}</p>
		</div>

		<section v-if="contextCard" class="context-card">
			<span class="section-label">{{ t("regulatoryAssistant.answer.context") }}</span>
			<p class="context-label">{{ contextCard.label }}</p>
			<div class="chips">
				<span v-if="contextCard.parcelId" class="chip highlighted">
					{{ t("regulatoryAssistant.answer.parcel", { parcelId: contextCard.parcelId }) }}
				</span>
				<span v-for="zoneCode in contextCard.zoneCodes" :key="zoneCode" class="chip">
					{{ t("regulatoryAssistant.answer.zone", { zoneCode }) }}
				</span>
				<span v-for="flag in contextCard.activeFlags" :key="flag" class="chip">{{ flag }}</span>
			</div>

			<div v-if="contextCard.planningPolicySources.length > 0" class="policies">
				<span class="section-label">{{ t("regulatoryAssistant.answer.applicableDocuments") }}</span>
				<div v-for="policy in contextCard.planningPolicySources" :key="policy.name" class="policy">
					<div class="policy-text">
						<p class="policy-name">{{ policy.name }}</p>
						<p v-if="policyMeta(policy)" class="secondary">{{ policyMeta(policy) }}</p>
					</div>
					<div class="links">
						<a v-if="policy.mapUrl" :href="policy.mapUrl" target="_blank" rel="noopener noreferrer" class="link">
							{{ t("regulatoryAssistant.answer.map") }}
							<AppIcon :name="IconName.ArrowUpRight" :size="12" />
						</a>
						<a
							v-if="policy.documentUrl"
							:href="policy.documentUrl"
							target="_blank"
							rel="noopener noreferrer"
							class="link"
						>
							{{ t("regulatoryAssistant.answer.document") }}
							<AppIcon :name="IconName.ArrowUpRight" :size="12" />
						</a>
					</div>
				</div>
			</div>
		</section>

		<template v-if="answer.categories.length === 0">
			<section v-for="section in fallbackSections" :key="section.key" class="fallback-section">
				<span class="section-label">{{ section.label }}</span>
				<ul>
					<li v-for="point in section.points" :key="point">{{ point }}</li>
				</ul>
			</section>
		</template>

		<AccordionRoot multiple :persistent="false" :default-value="defaultOpenSections" class="sections">
			<AccordionItem
				v-for="category in answer.categories"
				:key="category.key"
				:value="category.key"
				class="category"
				:style="categoryStyles[category.key]"
			>
				<template #title>
					<span class="category-title">{{ t(`regulatoryAssistant.answer.categories.${category.key}`) }}</span>
				</template>
				<template #header-actions>
					<span class="count">{{ t("regulatoryAssistant.answer.pointCount", category.items.length) }}</span>
				</template>
				<ul class="items">
					<li v-for="(item, index) in visibleItems(category)" :key="index" class="item">
						<p v-if="item.title" class="item-title">{{ item.title }}</p>
						<p v-if="item.detail">{{ item.detail }}</p>
						<div v-if="item.meta.length > 0" class="chips">
							<span v-for="meta in item.meta" :key="meta" class="chip">{{ meta }}</span>
						</div>
						<div v-if="item.links.length > 0" class="links">
							<a
								v-for="link in item.links"
								:key="link.href"
								:href="link.href"
								target="_blank"
								rel="noopener noreferrer"
								class="link"
							>
								{{ link.label }}
								<AppIcon :name="IconName.ArrowUpRight" :size="12" />
							</a>
						</div>
					</li>
				</ul>
				<button
					v-if="category.items.length > VISIBLE_ITEM_COUNT"
					type="button"
					class="toggle-items"
					@click="toggleCategory(category.key)"
				>
					{{
						expandedCategories.has(category.key)
							? t("regulatoryAssistant.answer.showLess")
							: t("regulatoryAssistant.answer.showMore", { count: category.items.length - VISIBLE_ITEM_COUNT })
					}}
					<AppIcon
						:name="IconName.ChevronDown"
						:size="14"
						class="toggle-chevron"
						:class="{ expanded: expandedCategories.has(category.key) }"
					/>
				</button>
			</AccordionItem>

			<AccordionItem v-if="response.references.length > 0" :value="REFERENCES_SECTION" class="references">
				<template #title>
					<span class="section-label">{{ t("regulatoryAssistant.answer.references") }}</span>
				</template>
				<template #header-actions>
					<span class="count">{{ response.references.length }}</span>
				</template>
				<div class="reference-list">
					<component
						:is="reference.url ? 'a' : 'div'"
						v-for="reference in response.references"
						:key="reference.id"
						class="reference"
						v-bind="reference.url ? { href: reference.url, target: '_blank', rel: 'noopener noreferrer' } : {}"
					>
						<div class="reference-text">
							<p class="reference-label">{{ reference.label }}</p>
							<p v-if="referenceMeta(reference)" class="secondary">{{ referenceMeta(reference) }}</p>
						</div>
						<AppIcon v-if="reference.url" :name="IconName.ArrowUpRight" :size="14" />
					</component>
				</div>
			</AccordionItem>
		</AccordionRoot>

		<p class="answer-meta">
			{{ t(`regulatoryAssistant.answer.confidence.${response.confidence}`) }} · {{ formatDuration(durationMs) }}
		</p>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import {
	RegulatoryAnswerCategoryKey,
	type PlanningPolicySource,
	type RegulatoryAnswerCategory,
	type RegulatoryAnswerItem,
	type RegulatoryAssistantResponse,
	type RegulatoryReference,
} from "@/types/regulatoryAssistant"
import { formatDuration } from "@/utils/format"

const props = defineProps<{
	response: RegulatoryAssistantResponse
	durationMs: number
}>()

const { t, te } = useI18n()

const categoryStyles: Record<RegulatoryAnswerCategoryKey, Record<string, string>> = {
	[RegulatoryAnswerCategoryKey.CurrentRights]: {
		"--category-color": "var(--accent-assistant-current-rights)",
		"--category-background": "var(--accent-assistant-current-rights-bg)",
	},
	[RegulatoryAnswerCategoryKey.RegulatoryPotential]: {
		"--category-color": "var(--accent-assistant-regulatory-potential)",
		"--category-background": "var(--accent-assistant-regulatory-potential-bg)",
	},
	[RegulatoryAnswerCategoryKey.SiteConstraints]: {
		"--category-color": "var(--accent-assistant-site-constraints)",
		"--category-background": "var(--accent-assistant-site-constraints-bg)",
	},
}

const VISIBLE_ITEM_COUNT = 3
const REFERENCES_SECTION = "references"

const answer = computed(() => props.response.answer)
const contextCard = computed(() => props.response.contextCard)
const expandedCategories = ref(new Set<RegulatoryAnswerCategoryKey>())

const defaultOpenSections = computed(() => [
	...answer.value.categories.slice(0, 1).map(category => category.key),
	...(props.response.references.length <= 2 ? [REFERENCES_SECTION] : []),
])

function visibleItems(category: RegulatoryAnswerCategory): RegulatoryAnswerItem[] {
	return expandedCategories.value.has(category.key) ? category.items : category.items.slice(0, VISIBLE_ITEM_COUNT)
}

function toggleCategory(key: RegulatoryAnswerCategoryKey): void {
	if (expandedCategories.value.has(key)) expandedCategories.value.delete(key)
	else expandedCategories.value.add(key)
}

const fallbackSections = computed(() =>
	[
		{ key: "keyPoints", label: t("regulatoryAssistant.answer.keyPoints"), points: answer.value.keyPoints },
		{ key: "parcelContext", label: t("regulatoryAssistant.answer.parcelContext"), points: answer.value.parcelContext },
		{ key: "zoneContext", label: t("regulatoryAssistant.answer.zoneContext"), points: answer.value.zoneContext },
		{ key: "watchouts", label: t("regulatoryAssistant.answer.watchouts"), points: answer.value.watchouts },
	].filter(section => section.points.length > 0)
)

function policyMeta(policy: PlanningPolicySource): string {
	return [
		policy.type,
		policy.version,
		policy.minUnitsPerHectare
			? t("regulatoryAssistant.answer.minUnitsPerHectare", { count: policy.minUnitsPerHectare })
			: null,
	]
		.filter(Boolean)
		.join(" · ")
}

function referenceMeta(reference: RegulatoryReference): string {
	const documentTypeKey = `regulatoryAssistant.answer.documentTypes.${reference.documentType}`
	const documentType = reference.documentType && te(documentTypeKey) ? t(documentTypeKey) : reference.documentType
	const label = reference.label.toLowerCase()
	return [
		documentType,
		reference.coverage,
		reference.articleRef ? t("regulatoryAssistant.answer.article", { articleRef: reference.articleRef }) : null,
	]
		.filter((part): part is string => !!part && !label.includes(part.toLowerCase()))
		.join(" · ")
}
</script>

<style scoped>
.regulatory-assistant-answer {
	display: flex;
	flex-direction: column;
	gap: var(--spacing-md);
	color: var(--neutral-900);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);

	.section-label {
		color: var(--neutral-600);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-semibold);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.secondary {
		color: var(--neutral-600);
		font-size: var(--font-size-xxs);
	}

	.chips {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);

		.chip {
			padding: var(--spacing-xxs) var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);

			&.highlighted {
				border-color: color-mix(in srgb, var(--primary-green) 30%, transparent);
				color: var(--primary-green);
			}
		}
	}

	.links {
		display: flex;
		flex-wrap: wrap;
		gap: var(--spacing-xs);

		.link {
			display: inline-flex;
			align-items: center;
			gap: var(--spacing-xxs);
			padding: var(--spacing-xxs) var(--spacing-sm);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background: var(--neutral-0);
			color: var(--neutral-900);
			font-size: var(--font-size-xxs);
			text-decoration: none;

			&:hover {
				border-color: var(--primary-green);
				color: var(--primary-green);
			}
		}
	}

	.context-card {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background: var(--neutral-0);

		.context-label {
			font-weight: var(--font-weight-medium);
		}

		.policies {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);
			padding-top: var(--spacing-sm);
			border-top: var(--border-width-thin) solid var(--neutral-200);

			.policy {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-xs);

				.policy-name {
					font-weight: var(--font-weight-medium);
				}
			}
		}
	}

	.heading {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);
		margin-bottom: calc(-1 * var(--spacing-xs));
		padding-top: var(--spacing-xs);

		.summary {
			white-space: pre-wrap;
		}
	}

	.sections {
		& :deep(.accordion-item) {
			box-shadow: none;
		}

		& :deep(.accordion-header) {
			gap: var(--spacing-sm);
		}

		& :deep(.accordion-item .accordion-trigger) {
			min-height: 0;
			padding: var(--spacing-sm) 0 var(--spacing-sm) var(--spacing-md);
			background: transparent;
		}

		& :deep(.accordion-content-wrapper) {
			background: transparent;

			&[data-state="open"] {
				height: auto;
			}
		}

		& :deep(.accordion-item .accordion-content) {
			padding: 0 var(--spacing-md) var(--spacing-sm);
			border-top: none;
			background: transparent;
		}

		.count {
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
		}
	}

	.category {
		border-color: color-mix(in srgb, var(--category-color) 20%, transparent);
		background: var(--category-background);

		.category-title {
			color: var(--neutral-600);
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
		}

		.items {
			display: flex;
			flex-direction: column;
			margin: 0;
			padding: 0;
			list-style: none;

			.item {
				display: flex;
				flex-direction: column;
				gap: var(--spacing-xs);
				padding: var(--spacing-sm) 0;

				& + .item {
					border-top: var(--border-width-thin) solid color-mix(in srgb, var(--category-color) 20%, transparent);
				}

				.item-title {
					font-weight: var(--font-weight-medium);
				}
			}
		}

		.toggle-items {
			display: flex;
			align-items: center;
			gap: var(--spacing-xs);
			width: 100%;
			padding: var(--spacing-sm) 0 0;
			border: none;
			border-top: var(--border-width-thin) solid color-mix(in srgb, var(--category-color) 20%, transparent);
			background: none;
			color: var(--category-color);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-xs);
			font-weight: var(--font-weight-semibold);
			cursor: pointer;

			&:hover {
				text-decoration: underline;
			}

			.toggle-chevron {
				transition: transform 0.2s ease;

				&.expanded {
					transform: rotate(180deg);
				}
			}
		}
	}

	.fallback-section {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xs);

		ul {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xxs);
			margin: 0;
			padding-left: var(--spacing-md);
		}
	}

	.references {
		& :deep(.accordion-content-wrapper .accordion-content) {
			padding: 0 var(--spacing-sm) var(--spacing-sm) var(--spacing-sm);
		}

		.reference-list {
			display: flex;
			flex-direction: column;
			gap: var(--spacing-xs);

			.reference {
				display: flex;
				align-items: flex-start;
				justify-content: space-between;
				gap: var(--spacing-sm);
				padding: var(--spacing-sm);
				border: var(--border-width-thin) solid var(--neutral-200);
				border-radius: var(--border-radius-md);
				color: var(--neutral-900);
				text-decoration: none;

				.reference-label {
					font-weight: var(--font-weight-medium);
					overflow-wrap: anywhere;
				}

				.app-icon {
					flex-shrink: 0;
					color: var(--neutral-600);
				}
			}

			a.reference:hover {
				border-color: var(--primary-green);

				.app-icon {
					color: var(--primary-green);
				}
			}
		}
	}

	.answer-meta {
		color: var(--neutral-600);
		font-size: var(--font-size-xxs);
		font-variant-numeric: tabular-nums;
	}
}
</style>
